import React, { useState, useMemo, useCallback } from 'react';

import * as d from 'date-fns';
import { Bar, BarChart, LabelList, ResponsiveContainer } from 'recharts';

import * as monthUtils from 'loot-core/src/shared/months';
import { integerToCurrency } from 'loot-core/src/shared/util';

import { theme, styles, type CSSProperties } from '../../../style';
import { Block } from '../../common/Block';
import { Link } from '../../common/Link';
import { View } from '../../common/View';
import { PrivacyFilter } from '../../PrivacyFilter';
import { Change } from '../Change';
import { chartTheme } from '../chart-theme';
import { DateRange } from '../DateRange';
import { LoadingIndicator } from '../LoadingIndicator';
import { ReportCard } from '../ReportCard';
import { simpleCashFlow } from '../spreadsheets/cash-flow-spreadsheet';
import { useReport } from '../useReport';

function CustomLabel({
  value,
  name,
  position,
  x,
  y,
  width: barWidth,
  height: barHeight,
}) {
  const valueLengthOffset = 20;

  const yOffset = barHeight < 25 ? 105 : y;

  const labelXOffsets = {
    right: 6,
    left: -valueLengthOffset + 1,
  };

  const valueXOffsets = {
    right: 6,
    left: -valueLengthOffset + 2,
  };

  const anchorValue = {
    right: 'start',
    left: 'end',
  };

  return (
    <>
      <text
        x={x + barWidth + labelXOffsets[position]}
        y={yOffset + 10}
        textAnchor={anchorValue[position]}
        fill={theme.tableText}
      >
        {name}
      </text>
      <text
        x={x + barWidth + valueXOffsets[position]}
        y={yOffset + 26}
        textAnchor={anchorValue[position]}
        fill={theme.tableText}
      >
        <PrivacyFilter>{integerToCurrency(value)}</PrivacyFilter>
      </text>
    </>
  );
}

type ICashFlowCardProps = {
  cardStyles?: CSSProperties;
  showViewAll?: boolean;
};

export function CashFlowCard({
  cardStyles = {},
  showViewAll,
}: ICashFlowCardProps) {
  const [dateRangeStart] = useState(d.startOfMonth(new Date()));
  const [dateRangeEnd] = useState(d.endOfMonth(new Date()));

  const params = useMemo(() => simpleCashFlow(dateRangeStart, dateRangeEnd), [dateRangeStart, dateRangeEnd]);
  const data = useReport('cash_flow_simple', params);

  const [isCardHovered, setIsCardHovered] = useState(false);
  const onCardHover = useCallback(() => setIsCardHovered(true), []);
  const onCardHoverEnd = useCallback(() => setIsCardHovered(false), []);

  const { graphData } = data || {};
  const expenses = -(graphData?.expense || 0);
  const income = graphData?.income || 0;

  return (
    <View id="cash-flow-card">
      <ReportCard
        flex={1}
        style={{ ...cardStyles }}
        {...(!showViewAll ? { to: '/reports/cash-flow' } : {})}
      >
        <View
          style={{ flex: 1 }}
          onPointerEnter={onCardHover}
          onPointerLeave={onCardHoverEnd}
        >
          <View style={{ flexDirection: 'row', padding: 20 }}>
            <View style={{ flex: 1 }}>
              <Block
                style={{
                  ...styles.mediumText,
                  fontWeight: 500,
                  marginBottom: 5,
                }}
                role="heading"
              >
                Real-time Cash Flow
              </Block>
              <DateRange start={dateRangeStart.toISOString()} end={dateRangeEnd.toISOString()} />
            </View>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {data && (
                <View style={{ textAlign: 'right' }}>
                  <PrivacyFilter activationFilters={[!isCardHovered]}>
                    <Change amount={income - expenses} />
                  </PrivacyFilter>
                </View>
              )}
              {showViewAll && (
                <Link to="/reports/cash-flow" variant="internal">
                  View more
                </Link>
              )}
            </div>
          </View>

          {data ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  {
                    income,
                    expenses,
                  },
                ]}
                margin={{
                  top: 10,
                  bottom: 0,
                }}
                {...(!graphData.income && !graphData.expense
                  ? { barGap: 32 }
                  : {})}
              >
                <Bar
                  dataKey="income"
                  fill={chartTheme.colors.green}
                  barSize={14}
                >
                  <LabelList
                    dataKey="income"
                    position="left"
                    content={<CustomLabel name="Credit" />}
                  />
                </Bar>
                <Bar
                  dataKey="expenses"
                  fill={chartTheme.colors.red}
                  barSize={14}
                >
                  <LabelList
                    dataKey="expenses"
                    position="right"
                    content={<CustomLabel name="Debit" />}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <LoadingIndicator />
          )}
        </View>
      </ReportCard>
    </View>
  );
}
